import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "../ui/drawer";
import { RESET_SONG_DRAWER, SET_SONG_DRAWER_OPEN, SET_SONG_DRAWER_SONG, UPDATE_SONG_PRIVACY } from "@/redux/features/music/songDrawerSlice";
import toast from "react-hot-toast";
import { SET_CLONESONG, SET_CREATEDESC, SET_CREATEGENRE, SET_CUSTOMDESC, SET_CUSTOMLYRICS, SET_CUSTOMTITLE, SET_DELETE_DIALOG_OPEN, SET_EXTENDSONG, SET_SELECTEDTAB, SET_SONG_TO_DELETE } from "@/redux/features/music/createSlice";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { SET_DIALOG_OPEN_TWO, SET_SONG_TO_ADD_TWO } from "@/redux/features/music/playlistSlice";
import { SET_DIALOG_OPEN, SET_SONG_TO_ADD } from "@/redux/features/music/leaderboardSlice";
import { Link } from "react-router-dom";
import { SET_SHOW_LOGIN } from "@/redux/features/auth/authSlice";
import { Trophy, Trash2, Globe, GlobeLock, ArrowUpDown, Music2, RefreshCw, Download, FolderPlus, Forward } from 'lucide-react';

function SongDrawer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isOpen, song, songsList, isPlaylist } = useSelector((state) => state.songDrawer);
  const { user } = useSelector((state) => state.auth);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const currentSong = song
    ? {
      ...song,
      publish: songsList[song.song_id]?.publish ?? song.publish,
    }
    : null;

  const author = user?.email === song?.user_id;

  // handle publish
  const handlePublish = async (e) => {
    if (!user || user?.is_guest) {
      dispatch(SET_SHOW_LOGIN(true))
      return toast.error('Login to publish the song')
    }
    e.stopPropagation();
    const token = window.localStorage.getItem("token");
    try {
      setLoading(true);
      toast.loading(currentSong?.publish ? "Unpublishing" : "Publishing", {
        id: "publish",
      });

      await axios.post(
        currentSong?.publish ? "/private" : "/public",
        { song_id: currentSong?.song_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newPublishState = !currentSong?.publish;
      // Update Redux state
      dispatch(
        UPDATE_SONG_PRIVACY({
          songId: currentSong.song_id,
          isPublic: newPublishState,
        })
      );
      toast.success(newPublishState ? "Published" : "Unpublished", { id: "publish" });
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.detail || "Error Occurred", { id: "publish" });
    } finally {
      setLoading(false);
    }
  };

  const handleExtend = (e) => {
    if (!user || user?.is_guest) {
      dispatch(SET_SHOW_LOGIN(true))
      return toast.error('Login to extend the song')
    }
    e.stopPropagation();
    dispatch(SET_SELECTEDTAB("extend"));
    dispatch(
      SET_EXTENDSONG({
        song_id: song?.song_id,
        song_name: song?.song_name,
      })
    );
    dispatch(RESET_SONG_DRAWER());
    navigate("/create");
  };

  const handleClone = (e) => {
    if (!user || user?.is_guest) {
      dispatch(SET_SHOW_LOGIN(true))
      return toast.error('Login to clone the song')
    }
    e.stopPropagation();
    dispatch(SET_SELECTEDTAB("clone"));
    dispatch(
      SET_CLONESONG({
        song_id: song?.song_id,
        song_name: song?.song_name,
      })
    );
    dispatch(RESET_SONG_DRAWER());
    navigate("/create");
  };

  const handleRecreate = (song) => {
    if (!user || user?.is_guest) {
      dispatch(SET_SHOW_LOGIN(true))
      return toast.error('Login to recreate the song')
    }
    if (
      song?.prompt === null ||
      song.prompt === "" ||
      song.prompt === undefined
    ) {
      toast.error("Feature not available on this song", { id: "recreate" });
      return;
    }
    if (song?.prompt?.type === "create" || song?.prompt?.type === "custom") {
      if (song.prompt.type === "create") {
        dispatch(SET_SELECTEDTAB("create"));
        dispatch(SET_CREATEDESC(song.prompt.desc));
        dispatch(SET_CREATEGENRE(song.prompt.genre.split(' ').map(tag => tag.charAt(0).toUpperCase() + tag.slice(1)).join(', ')))
        navigate("/create");
        dispatch(RESET_SONG_DRAWER());
      } else if (song.prompt.type === "custom") {
        dispatch(SET_SELECTEDTAB("custom"));
        dispatch(SET_CUSTOMDESC(song.prompt.genre));
        dispatch(SET_CUSTOMTITLE(song.prompt.desc));
        dispatch(SET_CUSTOMLYRICS(song.prompt.lyrics2));
        navigate("/create");
        dispatch(RESET_SONG_DRAWER());
      }
    } else {
      toast.error("Feature not available on this song", { id: "recreate" });
      return;
    }
  };

  function download(url) {
    const a = document.createElement("a");
    a.href = url;
    a.download = `Sound of Meme`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  const downloadSong = async () => {
    if (!user || user?.is_guest) {
      dispatch(SET_SHOW_LOGIN(true))
      return toast.error('Login to download the song')
    }
    setDownloadLoading(true);
    const token = window.localStorage.getItem("token") ;
    try {
      toast.loading("Starting Download", { id: "download" });
      const response = await axios.get(`/download?song_id=${song?.song_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const signedUrl = response.data.url;
      download(signedUrl);
      dispatch(RESET_SONG_DRAWER());
      toast.success("Download Started", { id: "download" });
    } catch (error) {
      console.error("Error fetching signed URL:", error);
      toast.error("Error Occurred. Please try again.", { id: "download" });
    } finally {
      setDownloadLoading(false);
    }
  };

  const AddToPlaylist = (e) => {
    if (!user || user?.is_guest) {
      dispatch(SET_SHOW_LOGIN(true))
      return toast.error('Login to add to playlist')
    }
    e.stopPropagation();
    dispatch(SET_SONG_TO_ADD_TWO(song));
    dispatch(SET_SONG_DRAWER_SONG(song));
    dispatch(SET_DIALOG_OPEN_TWO(true));
    dispatch(SET_SONG_DRAWER_OPEN(false));
    dispatch(RESET_SONG_DRAWER());
  };

  const handleAddToLeaderboard = (e) => {
    if (!user || user?.is_guest) {
      dispatch(SET_SHOW_LOGIN(true))
      return toast.error('Login to add to leaderboard', { id: "guest" })
    }
    e.stopPropagation();
    // setIsDrawerOpen(false);
    dispatch(SET_SONG_DRAWER_OPEN(false));
    dispatch(SET_SONG_TO_ADD(song));
    dispatch(SET_DIALOG_OPEN(true));
  };

  const handleOpenDeleteDialog = (e) => {
    e.stopPropagation();
    dispatch(SET_SONG_TO_DELETE(song));
    dispatch(SET_DELETE_DIALOG_OPEN(true));
    dispatch(SET_SONG_DRAWER_OPEN(false));
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      console.log('URL copied to clipboard!');
      toast.success('URL copied to clipboard!', { id: "copyToast" })
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };
  const handleShare = async (song) => {
    const url = `https://soundofmeme.com/song/${song?.slug}`;
    const title = 'Check out this song!';
    if (navigator.share) {
        try {
            await navigator.share({
                title,
                // text,
                url
            });
        } catch (error) {
            console.error('Error sharing:', error);
        }
    } else {
        copyToClipboard(url)
    }
};

  return (
    <Drawer open={isOpen} onOpenChange={(open) => dispatch(SET_SONG_DRAWER_OPEN(open))}>
      <DrawerContent className="font-poppins bg-gradient-to-br from-slate-950 to-slate-900 dark:bg-gradient-to-br dark:from-pink-300 dark:via-pink-200 dark:to-pink-300 text-slate-300 dark:text-slate-900 pb-8 border-x-0 border-green-400/50 px-4 rounded-t-3xl shadow-[0_-16px_16px_-1px_rgba(74,222,128,0.3)]">
        <div className="relative flex flex-col items-center justify-between gap-2 mt-8">
          <img src={song?.image_url} className="h-24 rounded-lg flex-shrink border border-green-400 shadow shadow-green-400/50 dark:border-pink-900 dark:shadow-md dark:shadow-pink-800/90" alt="Song cover" />
          <div className="flex-grow truncate">
            <DrawerTitle className={"text-slate-100 dark:text-pink-950 text-center font-medium truncate text-xl capitalize mt-2"}>
              {song?.song_name}
            </DrawerTitle>
            <DrawerDescription className={"select-none flex gap-2 text-xs font-semibold text-slate-400 dark:text-slate-800 justify-center "}>
     
              {song?.tags?.slice(0, 3)?.map((tag, index) => (
                <span key={index} className="capitalize truncate">{tag}</span>
              ))}
            </DrawerDescription>
            <div className="flex items-center justify-center gap-1 truncate mt-1.5" >
              <Link onClick={(e) => { e.stopPropagation(); dispatch(SET_SONG_DRAWER_OPEN(false)); }} to={`/profile/${song?.username}`} className="flex items-center gap-1 truncate" >
                {song?.profile_url ? <img src={song?.profile_url} alt="profile" className="w-5 h-5 rounded-full border border-green-400 dark:border-pink-900 flex-shrink-0" /> : <div className="w-5 h-5 rounded-full bg-green-400 dark:bg-pink-900 flex items-center justify-center border border-green-400 dark:border-pink-900 flex-shrink-0" >
                  <span className="text-black dark:text-pink-950 capitalize text-xs font-potta" >{song?.username?.charAt(0)}</span>
                </div>}
                <p className="text-sm text-slate-300 hover:text-green-400 dark:text-slate-800 dark:hover:text-pink-950 font-comforta capitalize truncate max-w-[100px]" >{song?.username}</p>
              </Link>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 xs:grid-cols-5 sm:grid-cols-6 gap-4 justify-items-center mt-8 mb-2">
          {author && !isPlaylist && (
            <button onClick={handlePublish} disabled={loading} className=" leading-tight text-xs rounded-lg text-teal-300 dark:text-teal-700  w-full flex items-center flex-col justify-center gap-2 font-medium">
              {!song?.publish ? (<><Globe size={60} className="border border-slate-600 shadow shadow-slate-600 rounded-xl p-4 bg-slate-900/80 dark:border-pink-900 dark:shadow-md dark:shadow-pink-800/90" /> Publish</>) : (<><GlobeLock size={60} className="text-red-400 border-2 border-slate-600 shadow shadow-slate-600 rounded-xl p-4 bg-slate-900/80 dark:border-pink-900 dark:shadow-md dark:shadow-pink-800/90" /><span className="text-red-400">Unpublish</span></>)}
          
            </button>
          )}
          <button onClick={() => handleShare(song)} type="button" className=" leading-tight text-xs rounded-lg text-green-400 dark:text-green-700  w-full flex items-center flex-col justify-center gap-2 font-bold">
            <Forward size={60} strokeWidth={3} className="border border-slate-600 font-bold shadow shadow-slate-600 rounded-xl p-4 bg-slate-900/80 dark:border-pink-900 dark:shadow-md dark:shadow-pink-800/90" /> Share
          </button>
          <button onClick={handleExtend} type="button" className=" leading-tight text-xs rounded-lg text-green-400 dark:text-green-700  w-full flex items-center flex-col justify-center gap-2 font-medium">
            <ArrowUpDown size={60} className="border border-slate-600 shadow shadow-slate-600 rounded-xl p-4 bg-slate-900/80 dark:border-pink-900 dark:shadow-md dark:shadow-pink-800/90" /> Extend
          </button>
          <button onClick={handleClone} type="button" className=" leading-tight text-xs rounded-lg text-teal-400 dark:text-teal-700  w-full flex items-center flex-col justify-center gap-2 font-medium">
            <Music2 size={60} className="border border-slate-600 shadow shadow-slate-600 rounded-xl p-4 bg-slate-900/80 dark:border-pink-900 dark:shadow-md dark:shadow-pink-800/90" /> Clone         
          </button>

          {(song?.prompt?.type === "create" || song?.prompt?.type === "custom") && (
            <button onClick={() => handleRecreate(song)} type="button" className=" leading-tight text-xs rounded-lg text-violet-400 dark:text-violet-700  w-full flex items-center flex-col justify-center gap-2 font-medium">
              <RefreshCw size={60} className="border border-slate-600 shadow shadow-slate-600 rounded-xl p-4 bg-slate-900/80 dark:border-pink-900 dark:shadow-md dark:shadow-pink-800/90" /> Recreate
            </button>
          )}
          <button disabled={downloadLoading} onClick={() => downloadSong(song)} type="button" className=" leading-tight text-xs rounded-lg text-cyan-400 dark:text-cyan-700  w-full flex items-center flex-col justify-center gap-2 font-medium">
            <Download size={60} className="border border-slate-600 shadow shadow-slate-600 rounded-xl p-4 bg-slate-900/80 dark:border-pink-900 dark:shadow-md dark:shadow-pink-800/90" /> Download
          </button>
         {!isPlaylist && <button onClick={AddToPlaylist} type="button" className=" leading-tight text-xs rounded-lg text-fuchsia-400 dark:text-fuchsia-700  w-full flex items-center flex-col justify-center gap-2 font-medium">
            <FolderPlus size={60} className="border border-slate-600 shadow shadow-slate-600 rounded-xl p-4 bg-slate-900/80 dark:border-pink-900 dark:shadow-md dark:shadow-pink-800/90" /> Add to Playlist
          </button>}

          {author && !isPlaylist && (
            <button onClick={handleAddToLeaderboard} type="button" className=" leading-tight text-xs rounded-lg text-emerald-400 dark:text-emerald-700  w-full flex items-center flex-col justify-center gap-2 font-medium">
              <Trophy size={60} className="border border-slate-600 shadow shadow-slate-600 rounded-xl p-4 bg-slate-900/80 dark:border-pink-900 dark:shadow-md dark:shadow-pink-800/90" /> Add to Leaderboard
            </button>
          )}
          {author && !isPlaylist && (
            <button onClick={handleOpenDeleteDialog} type="button" className=" leading-tight text-xs rounded-lg text-red-400 dark:text-red-700  w-full flex items-center flex-col justify-center gap-2 font-medium">
              <Trash2 size={60} className="border border-slate-600 shadow shadow-slate-600 rounded-xl p-4 bg-slate-900/80 dark:border-pink-900 dark:shadow-md dark:shadow-pink-800/90" /> Delete
            </button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default SongDrawer;
